import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../../../../../utils/config';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const JobOpeningsForm = ({ jobOpenings, setJobOpenings, selectedCareer, onSaved, onCancel, isModal }) => {
  const [formData, setFormData] = useState({
    title: '',
    department: '',
    description: '',
    requirements: '',
    responsibilities: '',
    employment_type: 'Full-time',
    location: '',
    meta_information: '',
  });

  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = sessionStorage.getItem('token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      };

      // Prepare payload for careers API
      const payload = {
        title: formData.title,
        description: formData.description,
        location: formData.location,
        department: formData.department,
        employment_type: formData.employment_type,
        requirements: formData.requirements,
        responsibilities: formData.responsibilities,
        meta_information: formData.meta_information, 
        };
      

      let response;
      if (selectedCareer && selectedCareer.id) {
        // Update existing career
        response = await axios.put(`${API_URL}/careers/${selectedCareer.id}`, payload, config);
      } else {
        // Create new career
        response = await axios.post(`${API_URL}/careers`, payload, config);
      }

      toast.success(selectedCareer && selectedCareer.id ? 'Job opening updated successfully!' : 'Job opening created successfully!');
      
      // Reset form
      setFormData({
        title: '',
        department: '',
        description: '',
        requirements: '',
        responsibilities: '',
        employment_type: 'Full-time',
        location: '',
        meta_information: '',
      });

      // Notify parent to update list
      if (onSaved && typeof onSaved === 'function') {
        onSaved(response.data, !!(selectedCareer && selectedCareer.id));
      } else {
        // fallback
        setJobOpenings((prev) => (selectedCareer && selectedCareer.id ? prev.map((c) => (c.id === response.data.id ? response.data : c)) : [response.data, ...prev]));
      }
    } catch (error) {
      const errorMessage = error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : 'Error creating job opening';
      toast.error(errorMessage);
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet',
  'link',
];


  useEffect(() => {
    if (selectedCareer) {
      setFormData((prev) => ({
        ...prev,
        title: selectedCareer.title || '',
        department: selectedCareer.department || '',
        description: selectedCareer.description || '',
        requirements: selectedCareer.requirements || '',
        responsibilities: selectedCareer.responsibilities || '',
        employment_type: selectedCareer.employment_type || 'Full-time',
        location: selectedCareer.location || '',
        meta_information: '',
      }));
    }
  }, [selectedCareer]);

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label htmlFor="title">Position Title *</label>
        <input
          type="text"
          className="form-control"
          id="title"
          name="title"
          value={formData.title}
          onChange={handleChange}
          required
          placeholder="e.g., Senior Software Engineer"
        />
      </div>

      <div className="form-group">
        <label htmlFor="department">Department *</label>
        <input
          type="text"
          className="form-control"
          id="department"
          name="department"
          value={formData.department}
          onChange={handleChange}
          required
          placeholder="e.g., Engineering"
        />
      </div>

      <div className="form-group">
        <label>Job Description *</label>
        <ReactQuill
          theme="snow"
          value={formData.description}
          onChange={(value) =>
            setFormData((prev) => ({ ...prev, description: value }))
          }
          modules={quillModules}
          formats={quillFormats}
        />
      </div>

      <div className="form-group">
        <label>Requirements *</label>
        <ReactQuill
          theme="snow"
          value={formData.requirements}
          onChange={(value) =>
            setFormData((prev) => ({ ...prev, requirements: value }))
          }
          modules={quillModules}
          formats={quillFormats}
        />
      </div>


      <div className="form-group">
        <label>Responsibilities *</label>
        <ReactQuill
          theme="snow"
          value={formData.responsibilities}
          onChange={(value) =>
            setFormData((prev) => ({ ...prev, responsibilities: value }))
          }
          modules={quillModules}
          formats={quillFormats}
        />
      </div>

      <div className="form-row">
        <div className="form-group col-md-6">
          <label htmlFor="employment_type">Employment Type *</label>
          <select
            className="form-control"
            id="employment_type"
            name="employment_type"
            value={formData.employment_type}
            onChange={handleChange}
            required
          >
            <option value="Full-time">Full-time</option>
            <option value="Part-time">Part-time</option>
            <option value="Contract">Contract</option>
            <option value="Temporary">Temporary</option>
            <option value="Internship">Internship</option>
          </select>
        </div>

        <div className="form-group col-md-6">
          <label htmlFor="location">Location *</label>
          <input
            type="text"
            className="form-control"
            id="location"
            name="location"
            value={formData.location}
            onChange={handleChange}
            required
            placeholder="e.g., Lagos, Nigeria"
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="meta_information">Salary Range</label>
        <input
          type="text"
          className="form-control"
          id="meta_information"
          name="meta_information"
          value={formData.meta_information}
          onChange={handleChange}
          placeholder="e.g., 50000-70000"
        />
      </div>

      <div className="d-flex gap-2">
        <button
          type="submit"
          className="btn btn-primary mr-2"
          disabled={loading}
        >
          {loading ? (selectedCareer && selectedCareer.id ? 'Updating...' : 'Creating...') : selectedCareer && selectedCareer.id ? 'Update Job Opening' : 'Create Job Opening'}
        </button>
        <button type="button" className="btn btn-secondary ml-2" onClick={onCancel}>
          {isModal ? 'Close' : 'Cancel'}
        </button>
      </div>
    </form>
  );
};

export default JobOpeningsForm;
